"use client"

import { useLayoutEffect, useState } from "react"

export interface ElementSize {
  width: number
  height: number
}

/**
 * Measures an element's content box and keeps it current through a ResizeObserver.
 * Attach the returned `ref` to the element; the size is zero until it has been measured,
 * so callers can wait for a real width before drawing.
 */
export function useElementSize<T extends Element>(): { ref: (el: T | null) => void; size: ElementSize; element: T | null } {
  const [element, setElement] = useState<T | null>(null)
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 })

  useLayoutEffect(() => {
    if (!element) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) setSize({ width, height })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return { ref: setElement, size, element }
}
